import { SymbolicaLogo } from "@/components/branding/symbolica-logo";
import { cn } from "@/lib/utils";
import { FormEvent, useState, ReactNode } from "react";
import Link from "next/link";

const MAILCHIMP_ENDPOINT =
  "https://symbolica.us9.list-manage.com/subscribe/post-json";
const MAILCHIMP_PARAMS = {
  u: "52c53e72c02d895c8078b80e9",
  id: "76da584473",
  fId: "008115e1f0",
};
const MAILCHIMP_B_FIELD = `b_${MAILCHIMP_PARAMS.u}_${MAILCHIMP_PARAMS.id}`;

export function LandingFooter({
  careers = true,
  className,
  lightMode = false,
  colorMode,
  noMargin = false,
  extraContent,
}: {
  careers?: boolean;
  className?: string;
  lightMode?: boolean;
  colorMode?: "light" | "dark" | "gray";
  noMargin?: boolean;
  /** Optional extra content to render below the email signup section */
  extraContent?: ReactNode;
}) {
  // colorMode takes precedence over lightMode for backward compatibility
  const mode = colorMode ?? (lightMode ? "light" : "dark");
  const isLightText = mode === "dark" || mode === "gray";
  const [email, setEmail] = useState("");
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);
  const [status, setStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [feedback, setFeedback] = useState("");

  const handleMailchimpSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!email.trim()) {
      setStatus("error");
      setFeedback("Please enter an email address.");
      return;
    }

    const callbackName = `mc_callback_${Date.now()}`;

    setStatus("loading");
    setFeedback("Subscribing...");

    if (typeof window === "undefined") {
      setStatus("error");
      setFeedback("Please try again in a moment.");
      return;
    }

    const cleanup = (script: HTMLScriptElement) => {
      delete (window as typeof window & Record<string, unknown>)[callbackName];
      script.remove();
    };

    const script = document.createElement("script");
    (window as typeof window & Record<string, unknown>)[callbackName] =
      (response: { result?: string; msg?: string }) => {
        if (response?.result === "success") {
          setStatus("success");
          setFeedback("Thanks! Check your inbox to confirm.");
          setEmail("");
        } else {
          setStatus("error");
          setFeedback(
            response?.msg?.replace?.(/<\/?[^>]+(>|$)/g, "") ??
              "Something went wrong. Please try again."
          );
        }
        cleanup(script);
      };

    const params = new URLSearchParams({
      u: MAILCHIMP_PARAMS.u,
      id: MAILCHIMP_PARAMS.id,
      f_id: MAILCHIMP_PARAMS.fId,
      c: callbackName,
      EMAIL: email.trim(),
    });

    params.append(MAILCHIMP_B_FIELD, "");
    params.append("subscribe", "");

    script.src = `${MAILCHIMP_ENDPOINT}?${params.toString()}`;
    script.async = true;
    script.onerror = () => {
      setStatus("error");
      setFeedback("Unable to reach the signup service. Please try again.");
      cleanup(script);
    };

    document.body.appendChild(script);
  };

  return (
    <footer
      className={cn(
        "relative isolate w-full border-t",
        !noMargin && "mt-24 md:mt-36",
        mode === "light"
          ? "border-black/10 bg-[#FFFDF0]"
          : mode === "gray"
            ? "border-white/10 bg-[#222]"
            : "border-white/10 bg-[#050505]"
      )}
    >
      <div
        className={cn(
          "mx-auto flex w-full ms flex-col gap-16 px-13 py-16 sm:px-15 lg:max-w-5xl xl:max-w-7xl lg:px-5 xl:px-8",
          className
        )}
      >
        <div className="flex flex-col gap-24 lg:justify-between lg:flex-row lg:gap-24">
          <div className="flex flex-col gap-6 lg:max-w-md">
            <p
              className={cn(
                "font-arizona-sans text-base leading-relaxed",
                isLightText ? "text-white" : "text-[#0a0a0a]"
              )}
            >
              Want to hear from us?
            </p>

            <form
              className="flex flex-col gap-3 sm:flex-row"
              onSubmit={handleMailchimpSubmit}
            >
              <input
                type="email"
                name="footer-email"
                placeholder="Email address"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className={cn(
                  "font-arizona-sans flex-1 min-w-[280px] rounded-lg border px-4 py-3 text-sm font-light focus:outline-none focus:ring-0 transition-colors duration-200",
                  isLightText
                    ? "border-white/[0.08] bg-transparent text-white placeholder:text-white/20 focus:border-white/20"
                    : "border-black/25 bg-black/5 text-[#0a0a0a] placeholder:text-black/40 focus:border-black/50"
                )}
              />
              <button
                type="submit"
                disabled={status === "loading"}
                className={cn(
                  "inline-flex items-center justify-center text-sm font-mono font-medium uppercase rounded-full px-6 py-3 transition-colors duration-200 cursor-pointer disabled:opacity-50 w-fit",
                  isLightText
                    ? "text-white border border-white/30 hover:border-white/60 bg-transparent"
                    : "text-[#0a0a0a] border border-[#0a0a0a]/30 hover:border-[#0a0a0a]/60 bg-transparent"
                )}
              >
                {status === "loading" ? "Submitting..." : "Submit"}
              </button>
            </form>
            {feedback && (
              <p
                className={cn(
                  "font-ibm-plex-mono text-xs",
                  status === "success" ? "text-emerald-600" : "text-red-500"
                )}
              >
                {feedback}
              </p>
            )}
            {extraContent}
          </div>

          {careers && (
            <div className="flex flex-col gap-6 max-w-md">
              <p
                className={cn(
                  "font-arizona-mix text-2xl font-light tracking-tight sm:text-[26px]",
                  isLightText ? "text-white" : "text-[#0a0a0a]"
                )}
              >
                Careers
              </p>

              <div
                className={cn(
                  "font-arizona-sans text-base leading-relaxed",
                  isLightText ? "text-white/70" : "text-black/70"
                )}
              >
                <p className="mb-3">
                  Symbolica is a research-focused, venture-backed startup
                  headquartered in the Bay Area.
                </p>
                <p>
                  If you&apos;ve ever been kept awake wondering what will come
                  after deep learning, or simply want to have a hand in building
                  the future of machine learning, we want to hear from you.
                </p>
              </div>

              <Link
                href="/careers"
                className={cn(
                  "group/btn inline-flex items-center gap-2.5 text-sm font-mono font-medium uppercase rounded-full px-6 py-3 transition-colors duration-200 w-fit",
                  isLightText
                    ? "text-white border border-white/30 hover:border-white/60 bg-transparent"
                    : "text-[#0a0a0a] border border-[#0a0a0a]/30 hover:border-[#0a0a0a]/60 bg-transparent"
                )}
              >
                See Jobs
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-3.5 h-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </Link>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-8 pt-10">
          <SymbolicaLogo
            className={cn(
              "h-10 w-fit justify-self-start",
              isLightText ? "text-white" : "text-[#0a0a0a]"
            )}
          />
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="flex flex-wrap gap-12">
              {[
                {
                  id: "linkedin",
                  label: "LinkedIn",
                  href: "https://www.linkedin.com/company/symbolica-ai/",
                },
                {
                  id: "x",
                  label: "X",
                  href: "https://twitter.com/symbolica",
                },
                {
                  id: "github",
                  label: "GitHub",
                  href: "https://github.com/symbolica-ai",
                },
                {
                  id: "contact",
                  label: "hello@symbolica.ai",
                  href: "mailto:hello@symbolica.ai",
                },
              ].map((link) => (
                <a
                  key={link.id}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onMouseEnter={() => setHoveredLink(link.id)}
                  onMouseLeave={() => setHoveredLink(null)}
                  onFocus={() => setHoveredLink(link.id)}
                  onBlur={() => setHoveredLink(null)}
                  className={cn(
                    "font-arizona-sans text-[15px] tracking-tight transition-colors",
                    hoveredLink === link.id
                      ? "text-periwinkle"
                      : isLightText
                        ? "text-white/60"
                        : "text-black/60"
                  )}
                >
                  {link.label}
                </a>
              ))}
            </div>
            <div
              className={cn(
                "flex flex-col sm:flex-row gap-4 sm:gap-8 font-arizona-sans text-sm",
                isLightText ? "text-white/40" : "text-black/40"
              )}
            >
              <p>Symbolica AI, Inc. © {new Date().getFullYear()}</p>
              <Link
                href="/terms/terms-of-use"
                className="hover:text-periwinkle transition-colors"
              >
                Terms
              </Link>
              <Link
                href="/terms/privacy-policy"
                className="hover:text-periwinkle transition-colors"
              >
                Privacy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
